import type { Concept } from '@yuan-math/curriculum';

/**
 * Grade 8 — the last band before Algebra I.
 *
 * The completion of the middle-school arc that `middle.ts` (Grade 6) and
 * `middle-upper.ts` (Grade 7) begin. Three things finish here:
 *
 *   - number stops being closed. Square roots produce quantities that are not
 *     ratios of integers at all, which is the first time a student meets a
 *     number they cannot write down exactly,
 *   - relationship becomes an object. A function is not a calculation to
 *     perform but a thing to have, compare and graph, and
 *   - geometry becomes argument. The Pythagorean theorem is the first result
 *     most students meet that is *proved* rather than measured.
 *
 * Two orderings are corrected relative to the placeholders these replace.
 * `functions-intro` now comes *before* `linear-functions` — you cannot say
 * which functions are linear before saying what a function is — and
 * `pythagoras` depends on `square-roots`, without which its answers cannot be
 * written at all.
 */
export const GRADE_EIGHT_CONCEPTS: Concept[] = [
  /* -------------------------------------------------------------- number */
  {
    id: 'exponent-rules',
    title: 'Laws of Exponents',
    summary: 'The rules for combining powers, every one of which is repeated multiplication counted carefully.',
    strand: 'algebra',
    levels: ['grade-8'],
    prerequisites: ['exponents', 'rational-numbers'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'scientific-notation',
    title: 'Scientific Notation',
    summary: 'Writing the very large and the very small so they can be compared at a glance.',
    strand: 'numbers',
    levels: ['grade-8'],
    prerequisites: ['exponent-rules', 'decimal-place-value'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'square-roots',
    title: 'Square Roots and Irrational Numbers',
    summary: 'Undoing a square — and discovering that most of the answers cannot be written as fractions.',
    strand: 'numbers',
    levels: ['grade-8'],
    prerequisites: ['exponents', 'rational-numbers'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'cube-roots',
    title: 'Cube Roots',
    summary: 'Undoing a cube — the operation that turns a volume back into a length, and the one root that keeps a minus sign.',
    strand: 'numbers',
    levels: ['grade-8'],
    prerequisites: ['square-roots'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ------------------------------------------------------------- algebra */
  {
    id: 'linear-equation-solutions',
    title: 'One Solution, None, or Infinitely Many',
    summary: 'What it means when the unknown disappears — and why that is an answer rather than a failure.',
    strand: 'algebra',
    levels: ['grade-8'],
    prerequisites: ['multi-step-equations'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ----------------------------------------------------------- functions */
  {
    id: 'functions-intro',
    title: 'Functions',
    summary: 'A rule that gives exactly one output for each input — a relationship you can hold rather than perform.',
    strand: 'functions',
    levels: ['grade-8'],
    prerequisites: ['coordinate-plane', 'algebraic-expressions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'linear-functions',
    title: 'Linear Functions and Slope',
    summary: 'Straight-line relationships, and what slope measures: how fast one quantity changes with another.',
    strand: 'functions',
    levels: ['grade-8'],
    prerequisites: ['functions-intro', 'multi-step-equations', 'proportional-relationships'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'comparing-functions',
    title: 'Linear and Nonlinear Functions',
    summary: 'Telling a constant rate from a changing one, and comparing two relationships given in two different forms.',
    strand: 'functions',
    levels: ['grade-8'],
    prerequisites: ['linear-functions'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'systems-of-equations',
    title: 'Systems of Equations',
    summary: 'Two conditions at once, and the single point that satisfies both.',
    strand: 'algebra',
    levels: ['grade-8', 'grade-9'],
    prerequisites: ['linear-functions'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ------------------------------------------------------------ geometry */
  {
    id: 'pythagoras',
    title: 'The Pythagorean Theorem',
    summary: 'The relationship between the sides of a right triangle — and the first result you can prove rather than measure.',
    strand: 'geometry',
    levels: ['grade-8'],
    prerequisites: ['square-roots', 'angles', 'area-triangles'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'coordinate-distance',
    title: 'Distance on the Coordinate Plane',
    summary: 'The gap between two points is the hypotenuse of a triangle nobody drew — so the theorem measures it.',
    strand: 'geometry',
    levels: ['grade-8'],
    prerequisites: ['pythagoras', 'coordinate-plane'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'transformations',
    title: 'Transformations and Congruence',
    summary: 'Sliding, turning, flipping and resizing — and what each one leaves unchanged.',
    strand: 'geometry',
    levels: ['grade-8', 'grade-10'],
    prerequisites: ['coordinate-plane', 'angles'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'angle-relationships',
    title: 'Angles, Parallel Lines and Similar Triangles',
    summary:
      'Why a transversal makes only two different angles, why every triangle sums to 180°, and why two equal angles are enough to fix a shape.',
    strand: 'geometry',
    levels: ['grade-8'],
    prerequisites: ['angles', 'transformations'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'round-solids',
    title: 'Volume of Cylinders, Cones and Spheres',
    summary: 'Three round solids whose volumes stand in one of the tidiest relationships in geometry.',
    strand: 'geometry',
    levels: ['grade-8', 'grade-10'],
    prerequisites: ['volume', 'circles'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ---------------------------------------------------------- statistics */
  {
    id: 'scatter-plots',
    title: 'Scatter Plots and Association',
    summary: 'Looking for a relationship between two measurements — and refusing to claim one causes the other.',
    strand: 'statistics',
    levels: ['grade-8'],
    prerequisites: ['coordinate-plane', 'data-displays'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'two-way-tables',
    title: 'Two-Way Tables',
    summary: 'Association between two things you can count but not measure — and the row that has to be the denominator.',
    strand: 'statistics',
    levels: ['grade-8'],
    prerequisites: ['data-displays', 'percentages'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* --------------------------------------------------------- probability */
  {
    id: 'experimental-probability',
    title: 'Experimental and Theoretical Probability',
    summary: 'What a die should do and what it actually did — two different numbers, and the reason they converge.',
    strand: 'probability',
    levels: ['grade-7', 'grade-8'],
    prerequisites: ['basic-probability'],
    status: 'available',
    estimatedMinutes: 30,
  },
];
